import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Alert } from 'antd';
import { ReloadOutlined, BugOutlined } from '@ant-design/icons';

import styles from './chatErrorBoundary.module.less';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

class SSEErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };

    this.handleRetry = this.handleRetry.bind(this);
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 11)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      errorInfo,
    });

    // Call the optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleRetry(): void {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  }

  handleReload(): void {
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className={styles.errorBoundary}>
          <Alert
            message="Chat Service Error"
            description="Something went wrong with the chat service. Please try again."
            type="error"
            showIcon
            icon={<BugOutlined />}
            className={styles.errorAlert}
          />

          <div className={styles.errorActions}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={this.handleRetry}
              className={styles.retryButton}
            >
              Try Again
            </Button>

            <Button
              type="default"
              onClick={this.handleReload}
              className={styles.reloadButton}
            >
              Reload Page
            </Button>
          </div>

          {this.props.showDetails && this.state.error && (
            <details className={styles.errorDetails}>
              <summary>Technical Details</summary>
              <div className={styles.errorContent}>
                <p>
                  <strong>Error:</strong> {this.state.error.message}
                </p>
                <p>
                  <strong>Error ID:</strong> {this.state.errorId}
                </p>
                {this.state.error.stack && (
                  <pre className={styles.errorStack}>
                    {this.state.error.stack}
                  </pre>
                )}
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default SSEErrorBoundary;
